import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import IdentityAvatarContainer from '../../../containers/global/identity/IdentityAvatarContainer';
import Button from '../../global/Button';
import Alert from '../../global/Alert';
import ContextualString from '../../contextual/ContextualString';
import {
  ALBUM_PRICE_MODE,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../../../constants/albums';
import { formatPriceForDisplay } from '../../../util/format-money';
import {
  allowsJoinTypeSelection,
  getAlbumFolderCheckoutItems,
  getAlbumFolderIdentityCheckoutPrice,
  getAlbumFolderTotalPrice,
  getDefaultJoinType,
  getSelectedIdentitiesNameString,
} from '../../../util/discover-album-folder';
import getConfig from '../../../util/config';
import Spinner from '../../global/Spinner';
import {
  albumFolderHasCharity,
  albumFolderIsPrePaid,
  albumFolderRequiresPhotoSearchOnboarding,
  albumFolderHasSearchSubscriptionContent,
  albumFolderHasPhotoFinderCreditsContent,
  isAlbumFolderPhotoFinderEnabled,
  isPrepaidOnlyAlbumFolder,
} from '../../../util/album-folder';
import CheckoutPrice from '../CheckoutPrice';
import { PPGD_JOIN_TYPE } from '../../../constants/discover-album-folder';
import { sentryCaptureMessage } from '../../../util/sentry';
import IdentityNameContainer from '../../../containers/global/identity/IdentityNameContainer';
import DiscoverAlbumFolderJoinTypeSelectionModal from '../../../containers/discover/DiscoverAlbumFolderJoinTypeSelectionModal';
import AccountUpdatePhoneNumberModalContainer from '../../../containers/auth/AccountUpdatePhoneNumberModalContainer';
import { getAlbumCustomPrintName } from '../../../util/custom-prints';
import { gtm } from '../../../util/analytics';
import { isEligibleForShutterbugFreeTrial } from '../../../apps/temp/views/onboarding/PhotoSearchCheckout/helpers';
import TrashIcon from '../../icons/TrashIcon';
import { STRIPE_CHECKOUT_MODAL } from '../../global/StripeCheckout';
import WaldoSpeechBubble from '../../global/WaldoSpeechBubble';
import Badge from '../../global/Badge';
import { ALBUM_IDENTITY_ACCESS } from '../../../constants/identity';
import OnboardingLayoutFooter from '../../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import PriceDisplay from '../../global/PriceDisplay';
import { DISCOUNT_MODE } from '../../../util/promo-codes';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { SHUTTERBUG_TRIAL_DAYS_FALLBACK } from '../../../apps/temp/views/onboarding/PhotoSearchUpsell/helpers';
import PlusIcon from '../../icons/PlusIcon';
import {
  getCheckoutCartItemName,
  getCheckoutCartItemPrice,
} from '../../../services/checkout/redux/helpers';
import DiscoverAlbumFolderUserAddressContainer from '../../../containers/discover/DiscoverAlbumFolderUserAddressContainer';
import SiblingDiscountAlertModal, {
  SIBLING_DISCOUNT_ALERT_MODAL,
} from '../../discover/SiblingDiscountAlert';
import { closeModal } from '../../../actions/modal-actions';
import { CURRENCY_SYMBOLS } from '../../../constants/currency';

class AlbumFolderPurchaseSummary extends Component {
  static propTypes = {
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    albumFolder: PropTypes.shape({
      account: PropTypes.shape({
        organization_name: PropTypes.string,
      }),
      is_demo: PropTypes.bool,
      album_type: PropTypes.oneOf(Object.values(ALBUM_TYPE)),
      album_subtype: PropTypes.oneOf(Object.values(ALBUM_SUBTYPE)),
      has_charity: PropTypes.bool,
      charity_name: PropTypes.string,
      charity_percentage: PropTypes.number,
      album_price_mode: PropTypes.oneOf(Object.values(ALBUM_PRICE_MODE)),
      join_without_purchase: PropTypes.bool,
      day_pricings_discount_percentage: PropTypes.number,
      siblings_discount_percentage: PropTypes.number,
    }),
    selectedIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        relationship: PropTypes.string,
        albumFolders: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            album_subtype: PropTypes.oneOf(Object.values(ALBUM_SUBTYPE)),
          }),
        ).isRequired,
      }),
    ).isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    success: PropTypes.bool,
    checkout: PropTypes.func.isRequired,
    joinByCode: PropTypes.func.isRequired,
    checkoutAlbumFolder: PropTypes.func.isRequired,
    finalPrice: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.string,
    }),
    loadingFinalPrice: PropTypes.bool,
    checkoutPriceError: PropTypes.object,
    openModal: PropTypes.func.isRequired,
    location: PropTypes.shape({
      state: PropTypes.shape({
        fromDiscovery: PropTypes.bool,
      }),
    }),
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
    resetAlbumDiscovery: PropTypes.func,
    refetchMeWithIdentities: PropTypes.func,
    onSelectIdentity: PropTypes.func,
    onDeselectIdentity: PropTypes.func,
    subscribedAlbumFolderIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        album_folder: PropTypes.shape({
          id: PropTypes.string,
        }),
        identity: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    ),
    setJoinTypeSelectionFlag: PropTypes.func.isRequired,
    albumFolderJoin: PropTypes.func.isRequired,
  };

  state = {
    identityToEdit: null,
    skipPrintPackages: false,
  };

  componentDidMount() {
    this.checkForSiblingDiscount();
  }

  componentDidUpdate(prevProps) {
    const {
      checkoutPriceError,
      user,
      loadingFinalPrice,
      finalPrice,
      success,
      selectedIdentities,
    } = this.props;

    if (checkoutPriceError) {
      sentryCaptureMessage(checkoutPriceError, {
        level: 'error',
        extra: {
          accountId: user.id,
        },
      });
    }

    if (!loadingFinalPrice && finalPrice == null) {
      sentryCaptureMessage(
        '[AlbumFolderPurchase]: Final price failed to load',
        {
          level: 'error',
          extra: {
            accountId: user.id,
          },
        },
      );
    }

    // TODO: Once we re-enable discovery, we can show this again.
    const redirectOnSuccess = false;
    if (redirectOnSuccess && success && !prevProps.success) {
      this.onSuccess();
    }

    if (prevProps.selectedIdentities?.length !== selectedIdentities?.length) {
      this.checkForSiblingDiscount();
    }
  }

  componentWillUnmount() {
    const { success, resetAlbumDiscovery } = this.props;

    // Make sure after success we reset the overall state.
    if (success) {
      resetAlbumDiscovery();
    }
  }

  checkForSiblingDiscount = () => {
    const { albumFolder, selectedIdentities, openModal } = this.props;

    if (
      ((albumFolder?.album_subtype === ALBUM_SUBTYPE.CAMPS &&
        albumFolder?.day_pricings_discount_percentage > 0) ||
        albumFolder?.siblings_discount_percentage > 0 ||
        albumFolder?.siblings_discount_percentages?.length > 0) &&
      selectedIdentities?.length === 1
    ) {
      openModal(SIBLING_DISCOUNT_ALERT_MODAL);
    }
  };

  onSuccess = async () => {
    const {
      refetchMeWithIdentities,
      resetAlbumDiscovery,
      history,
      albumFolder,
    } = this.props;

    // TODO: This should be removed. We should investigate
    // why is it that the refetch is not triggering loading on RequirePurchasedAlbumFolder.
    await refetchMeWithIdentities();
    resetAlbumDiscovery();
    history.push(`/discover/album-folder/${albumFolder.root.id}`);
  };

  onCheckout = (stripeToken = {}) => {
    const {
      user,
      selectedIdentities,
      checkout,
      prepaidCheckoutItems,
      checkoutAlbumFolder,
      albumFolder,
      joinByCode,
      openModal,
      history,
      albumFolderJoin,
      isPlicCheckout,
      waldoPlusMonthlySubscription,
      refetchAccount,
      finalPrice,
    } = this.props;

    const identityIds = selectedIdentities.map(({ id }) => id);
    const hasSearchSubscription =
      (user?.search_subscriptions?.records?.length ?? 0) > 0;
    const purchaseItems = isPrepaidOnlyAlbumFolder(albumFolder)
      ? {
          photoItems: prepaidCheckoutItems?.photo_items ?? [],
          ppaItems: prepaidCheckoutItems?.subscription_items ?? [],
          photoPrintItems: prepaidCheckoutItems?.photo_print_items ?? [],
          customPrintItems: prepaidCheckoutItems?.custom_print_items ?? [],
          digitalBundleItems: prepaidCheckoutItems?.digital_bundle_items ?? [],
        }
      : getAlbumFolderCheckoutItems(selectedIdentities);

    if (isPrepaidOnlyAlbumFolder(albumFolder) && !prepaidCheckoutItems) {
      return;
    }

    if (
      isPlicCheckout &&
      !hasSearchSubscription &&
      waldoPlusMonthlySubscription &&
      albumFolder.search_subscription_credits_joins_enabled
    ) {
      openModal(STRIPE_CHECKOUT_MODAL, {
        checkoutPriceVariables: ({ promo_code: promoCode }) => {
          return {
            search_subscription_items: [
              {
                search_subscription_item_id: waldoPlusMonthlySubscription?.id,
              },
            ],
            promo_code: promoCode,
          };
        },
        accountReferralTypes: [
          ACCOUNT_REFERRAL_TYPE.SHUTTERBUG_PROMO,
          ACCOUNT_REFERRAL_TYPE.TIER_1_SHUTTERBUG_PROMO,
          ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS,
          ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS,
        ],
        onSuccess: token => {
          checkoutAlbumFolder({
            accountId: user.id,
            accountAddressId: user?.addresses?.[0]?.id,
            email: token.email,
            stripeToken: token.id,
            purchaseItems: {
              ...purchaseItems,
              searchSubscriptionItems: [
                {
                  search_subscription_item_id: waldoPlusMonthlySubscription.id,
                },
              ],
              promoCode: token.promo_code,
            },
            identityIdsToJoin: identityIds,
            albumFolderJoinCode: albumFolder.join_code,
            albumFolderId: albumFolder.id,
            checkoutMutation: checkout,
            joinByCodeMutation: joinByCode,
            albumFolderJoinMutation: albumFolderJoin,
            refetchAccountQuery: refetchAccount,
          });
        },
        defaultName: user?.name ?? '',
        defaultEmail: user?.email_address ?? '',
      });

      return;
    }

    if (
      albumFolderRequiresPhotoSearchOnboarding(albumFolder) &&
      !isPlicCheckout
    ) {
      history.push(
        `/discover/album-folder/${albumFolder.id}/photo-search-checkout`,
      );
      return;
    }

    checkoutAlbumFolder({
      accountId: user.id,
      accountAddressId: user?.addresses?.[0]?.id,
      email: stripeToken.email,
      stripeToken: stripeToken.id,
      stripePaymentMethodId: stripeToken.stripe_payment_method_id,
      stripeConnectAccountId:
        stripeToken.stripe_connect_account_id ??
        finalPrice?.stripe_connect_account_id,
      purchaseItems: {
        ...purchaseItems,
        promoCode: stripeToken.promo_code,
      },
      identityIdsToJoin: identityIds,
      albumFolderJoinCode: albumFolder.join_code,
      albumFolderId: albumFolder.id,
      checkoutMutation: checkout,
      joinByCodeMutation: joinByCode,
      albumFolderJoinMutation: albumFolderJoin,
    });
  };

  onIdentityJoinTypeEdit = identityToEdit => {
    this.setState(() => ({
      identityToEdit,
    }));
  };

  renderIdentity = identity => {
    const {
      albumFolder,
      subscribedAlbumFolderIdentities,
      user,
      setActiveIdentity,
      onDeselectIdentity,
      getIdentityCartItems,
      onIdentityCartItemsRemove,
      selectedIdentities,
      allowsPrepaidItems,
      history,
    } = this.props;
    const isSubscribed = subscribedAlbumFolderIdentities.some(
      subscribedItem => subscribedItem?.identity?.id === identity.id,
    );
    const [albumCustomPrint] = identity.selectedAlbumCustomPrints || [];
    const price = getAlbumFolderIdentityCheckoutPrice(identity);
    const hasSearchSubscription =
      (user?.search_subscriptions?.records?.length ?? 0) > 0;
    const isPrePaid = albumFolderIsPrePaid(albumFolder);
    const identityCartItems = getIdentityCartItems({ identityId: identity.id });

    return (
      <div key={identity.id}>
        <div className="flex-list-item w-100 px-0">
          <IdentityAvatarContainer className="mr-2" identity={identity} />

          <div className="d-flex flex-column align-items-start flex-list-item-truncate-30">
            <IdentityNameContainer
              className="text-dark text-extra-bold text-truncate"
              identity={identity}
            />

            {allowsJoinTypeSelection(albumFolder, allowsPrepaidItems) &&
              // If we have a default join type selection do not allow for changing it.
              !getDefaultJoinType(albumFolder) &&
              !isSubscribed && (
                <Button
                  btnSize="xs"
                  btnStyle="link"
                  className="p-0"
                  onClick={() => this.onIdentityJoinTypeEdit(identity)}
                >
                  Edit Selection
                </Button>
              )}
          </div>
          {albumFolder.album_subtype === ALBUM_SUBTYPE.EVENTS && (
            <div className="text-sm discover-album-folder-container ml-auto p-1 w-auto">
              <span className="text-sm ml-auto">
                <ContextualString
                  id="purchaseSubscriptionText"
                  type={albumFolder.album_type}
                  subtype={albumFolder.album_subtype}
                  defaultMessage="Photo Pass"
                />
              </span>
            </div>
          )}
          {albumFolder.album_subtype !== ALBUM_SUBTYPE.EVENTS && (
            <>
              {isAlbumFolderPhotoFinderEnabled(albumFolder) || isPrePaid ? (
                <div className="text-sm discover-album-folder-container ml-auto p-1 w-auto">
                  <div className="text-sm ml-auto d-flex align-items-center">
                    {(() => {
                      if (
                        // SB + PF + Sub
                        albumFolderHasSearchSubscriptionContent(albumFolder) &&
                        albumFolderHasPhotoFinderCreditsContent(albumFolder) &&
                        hasSearchSubscription
                      ) {
                        return (
                          <span className="text-primary">
                            Included in Subscription
                          </span>
                        );
                      }

                      if (
                        // SB + PF - Sub
                        albumFolderHasSearchSubscriptionContent(albumFolder) &&
                        albumFolderHasPhotoFinderCreditsContent(albumFolder) &&
                        !hasSearchSubscription
                      ) {
                        return (
                          <>
                            <span className="text-primary">Join</span>
                          </>
                        );
                      }

                      if (
                        !albumFolderHasSearchSubscriptionContent(albumFolder)
                      ) {
                        // PF - Free sub
                        // Pre-paid
                        return <span className="text-primary">Join</span>;
                      }

                      // SB, SB+PF if subbed
                      return (
                        <span className="text-primary">
                          Included in Subscription
                        </span>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <Fragment>
                  {!allowsJoinTypeSelection(albumFolder) &&
                  identity.joinType === PPGD_JOIN_TYPE.JOIN ? (
                    // This ensures the Trash icon is at the right
                    // without disrupting any of the other configs
                    <div className="ml-auto p-1" />
                  ) : (
                    <div className="text-sm discover-album-folder-container ml-auto p-1 w-auto">
                      <span className="text-sm ml-auto d-flex flex-column align-items-end text-sm">
                        <span className="text-dark ml-1">
                          <span>
                            {identity.joinType === PPGD_JOIN_TYPE.PURCHASE ? (
                              <Fragment>
                                <ContextualString
                                  id="purchaseSubscriptionText"
                                  type={albumFolder.album_type}
                                  subtype={albumFolder.album_subtype}
                                  defaultMessage="Photo Pass"
                                />
                                {' - '}
                              </Fragment>
                            ) : (
                              'Preview - '
                            )}
                          </span>
                          {price === 0 ||
                          identity.joinType === PPGD_JOIN_TYPE.JOIN ||
                          albumFolderIsPrePaid(albumFolder) ? (
                            <span className="text-primary">FREE</span>
                          ) : (
                            ` - $${formatPriceForDisplay(price)}`
                          )}
                        </span>

                        {identity?.selectedAlbumCustomPrints?.length > 0 && (
                          <span className="ml-auto text-xs">
                            <span className="text-italic">plus</span>{' '}
                            {getAlbumCustomPrintName(albumCustomPrint)}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </Fragment>
              )}
            </>
          )}

          <Button
            btnStyle="reset"
            type="button"
            className="ml-1 d-flex"
            onClick={() => {
              onDeselectIdentity(identity);
              onIdentityCartItemsRemove?.({ identityId: identity.id });
              if (selectedIdentities.length === 1) {
                history.replace(
                  `/purchase/album-folder/${albumFolder.id}/purchase-subscription`,
                );
              }
            }}
          >
            <TrashIcon size="15" color="#FE5851" />
          </Button>
        </div>
        {isPrepaidOnlyAlbumFolder(albumFolder) && (
          <div className="mt-1">
            {identityCartItems.map(cartItem => {
              const itemPrice = getCheckoutCartItemPrice(cartItem);

              return (
                <div
                  key={cartItem.id}
                  className="w-100 d-flex align-items-center justify-content-between"
                >
                  <span>
                    {cartItem.quantity ?? 1}x{' '}
                    {getCheckoutCartItemName(cartItem)}
                  </span>
                  <span>
                    {CURRENCY_SYMBOLS[itemPrice?.currency ?? 'USD']}
                    {formatPriceForDisplay(itemPrice?.value ?? 0)}
                  </span>
                </div>
              );
            })}
            <Button
              btnSize="sm"
              btnStyle="link"
              className="text-extra-bold"
              onClick={() => {
                setActiveIdentity(identity);
                history.replace(
                  `/purchase/album-folder/${albumFolder.id}/pre-purchase`,
                );
              }}
            >
              Update selection
            </Button>
          </div>
        )}
      </div>
    );
  };

  getAccountReferralTypes = () => {
    const { checkoutItems, prepaidCheckoutItems } = this.props;

    const referralTypes = [];

    if (
      (prepaidCheckoutItems?.digital_bundle_items ?? []).length > 0 ||
      (prepaidCheckoutItems?.photo_download_items ?? []).length > 0 ||
      !!(prepaidCheckoutItems?.photo_items ?? []).find(
        i => !!i.digital_downloads_purchase,
      )
    ) {
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.DIGITAL_DOWNLOADS);
    }

    if (
      (checkoutItems?.customPrintItems ?? []).length > 0 ||
      (prepaidCheckoutItems?.custom_print_items ?? []).length > 0 ||
      (prepaidCheckoutItems?.photo_items ?? []).length > 0 ||
      (prepaidCheckoutItems?.photo_print_items ?? []).length > 0
    ) {
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.PRINTS);
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS);
    }

    if (
      (checkoutItems?.ppaItems ?? []).length > 0 ||
      (prepaidCheckoutItems?.subscription_items ?? []).length > 0
    ) {
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS);
      if (!referralTypes.includes(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS)) {
        referralTypes.push(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS);
      }
    }

    return referralTypes;
  };

  render() {
    const { identityToEdit } = this.state;
    const {
      selectedIdentities,
      albumFolder,
      loading,
      error,
      finalPrice,
      checkoutItems,
      prepaidCheckoutItems,
      loadingFinalPrice,
      checkoutPriceError,
      openModal,
      location,
      success,
      onSelectIdentity,
      history,
      setJoinTypeSelectionFlag,
      user,
      userAddress,
      prepaidRequiresAddress,
      allowsPrepaidItems,
      prepaidPrintPackagePromoCode,
      clearCart,
    } = this.props;

    const { skipPrintPackages } = this.state;

    const config = getConfig();
    const isPrePaid = albumFolderIsPrePaid(albumFolder);
    const hasCharity = albumFolderHasCharity(albumFolder);
    const isPictureDay = !!albumFolder?.picture_day_enabled;
    const { baseUrl } = config?.app ?? {};
    const hasSearchSubscription =
      (user?.search_subscriptions?.records?.length ?? 0) > 0;

    if (loading || !albumFolder) {
      return (
        <div>
          <Spinner />
        </div>
      );
    }

    if (success) {
      if (
        albumFolderHasSearchSubscriptionContent(albumFolder) &&
        !hasSearchSubscription
      ) {
        // If they get to this piece of code, it means that:
        // a) The gallery has some SB content
        // b) There is *OTHER* type of content of some sort (i.e, PF).
        //
        // SB-only content galleries will be caught by the albumFolderRequiresPhotoSearchOnboarding
        // and dealt with separately.
        history.replace(`/purchase/album-folder/${albumFolder.id}/shutterbug`);
        return null;
      }

      // If the gallery allows for Prepaid Print Packages post-purchase,
      // direct the user to the Print Package Purchase page.
      if (
        !isPrepaidOnlyAlbumFolder(albumFolder) &&
        allowsPrepaidItems &&
        !skipPrintPackages
      ) {
        history.replace(
          `/discover/album-folder/${albumFolder.id}/pre-purchase?skippable=1`,
        );
        return null;
      }

      if (isPrepaidOnlyAlbumFolder(albumFolder)) {
        clearCart?.();
      }
      history.replace(`/purchase/album-folder/${albumFolder.id}/success`);
    }

    const identitiesForJoin = selectedIdentities.filter(
      identity => identity.joinType === PPGD_JOIN_TYPE.JOIN,
    );

    const canCheckout =
      !loading &&
      !loadingFinalPrice &&
      !checkoutPriceError &&
      (!prepaidRequiresAddress || !!userAddress);
    const identityNames = getSelectedIdentitiesNameString(selectedIdentities);

    const checkoutPriceVariables = isPrepaidOnlyAlbumFolder(albumFolder)
      ? prepaidCheckoutItems
      : {
          subscription_items: checkoutItems.ppaItems,
          custom_print_items: checkoutItems.customPrintItems,
        };

    return (
      <Fragment>
        <WaldoSpeechBubble className="w-100-i mb-4" position="right">
          {allowsPrepaidItems ? (
            <Fragment>
              Add additional{' '}
              {
                <ContextualString
                  id="identityReference"
                  defaultMessage="person"
                  type={albumFolder?.album_type}
                  subtype={albumFolder?.album_subtype}
                  formatter={contextualString => pluralize(contextualString, 2)}
                />
              }{' '}
              or select your purchase option below!
            </Fragment>
          ) : (
            <Fragment>
              You will receive matched photos in {albumFolder?.name} for{' '}
              {identityNames}!
            </Fragment>
          )}
        </WaldoSpeechBubble>

        {loading ? (
          <Spinner />
        ) : (
          <Fragment>
            <div className="discovery-album-folder-summary-content">
              <div className="discovery-album-folder-summary-scroll-wrapper">
                <div className="discovery-album-folder-summary-scroll">
                  {hasCharity && !isPrePaid && !isPictureDay && (
                    <Alert type="info" className="mt-2 mb-0">
                      {(albumFolder && albumFolder.charity_description) || (
                        <ContextualString
                          id="albumFolderPurchaseCharityDefaultMessage"
                          type={albumFolder && albumFolder.album_type}
                          subtype={albumFolder && albumFolder.album_subtype}
                          defaultMessage="*50% goes to charity. Thank you!"
                        />
                      )}
                    </Alert>
                  )}

                  {isPrePaid &&
                    albumFolder.album_subtype !== ALBUM_SUBTYPE.EVENTS && (
                      <Alert type="info" className="mt-2 mb-0">
                        <div className="d-flex align-items-center flex-column">
                          <p className="mb-0 text-center text-dark">
                            Join your faces below to {albumFolder.name}!
                            I&rsquo;ll find and deliver your photos!
                          </p>
                        </div>
                      </Alert>
                    )}

                  <div>
                    {error && <Alert type="danger">{error}</Alert>}
                    {checkoutPriceError && (
                      <Alert type="danger">
                        <p>
                          Whoops! There was an error processing this request.
                          Please try again in a moment.
                        </p>
                        <p>Error info: {checkoutPriceError}</p>
                      </Alert>
                    )}
                    {selectedIdentities.map(identity =>
                      this.renderIdentity(identity),
                    )}
                  </div>

                  {hasCharity &&
                    !isPictureDay &&
                    albumFolder.charity_footnote && (
                      <p className="text-xs text-muted text-center charity-footnote">
                        {albumFolder.charity_footnote}
                      </p>
                    )}
                  {prepaidRequiresAddress && (
                    <DiscoverAlbumFolderUserAddressContainer
                      address={userAddress}
                      userId={user.id}
                      className="mt-2"
                      addressDisplayTitle="Shipped to:"
                    />
                  )}
                </div>
              </div>
              {selectedIdentities.length > 0 &&
                finalPrice?.total_price?.value > 0 && (
                  <Fragment>
                    <hr className="my-2" />
                    <CheckoutPrice
                      finalPrice={finalPrice?.total_price?.value}
                      taxPrice={finalPrice?.total_tax?.value}
                      shippingPrice={finalPrice?.total_shipping_cost?.value}
                      totalPrice={Math.max(
                        finalPrice?.total_price?.value +
                          (finalPrice?.total_discount_price?.value ?? 0),
                        getAlbumFolderTotalPrice(selectedIdentities),
                      )}
                      loading={loadingFinalPrice}
                    />
                  </Fragment>
                )}
              {selectedIdentities.length > 0 &&
                albumFolder?.album_identity_access !==
                  ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY && (
                  <Fragment>
                    <Button
                      btnStyle="link"
                      btnBlock
                      className="text-extra-bold mt-2 d-flex align-items-center"
                      onClick={() => {
                        // If the album folder allows it we want to re-enable
                        // join type selection in the identity screen (in case
                        // it was disabled).
                        if (allowsJoinTypeSelection(albumFolder)) {
                          setJoinTypeSelectionFlag(true);
                        }
                        history.replace(
                          `/purchase/album-folder/${albumFolder.id}/purchase-subscription`,
                        );
                      }}
                    >
                      <PlusIcon className="mr-1" color="#0d84fe" size={14} />
                      Add more{' '}
                      <ContextualString
                        id="identityReference"
                        type={albumFolder?.album_type}
                        subtype={albumFolder?.album_subtype}
                        formatter={str => pluralize(str, 2)}
                      />
                    </Button>
                  </Fragment>
                )}
            </div>

            <OnboardingLayoutFooter fixed>
              <div className="discover-album-folder-checkout-btn-wrapper">
                {finalPrice?.total_price?.value > 0 ? (
                  <Fragment>
                    <Button
                      disabled={!canCheckout}
                      btnBlock
                      className="mt-1"
                      onClick={() => {
                        if (!canCheckout) {
                          return;
                        }

                        openModal(STRIPE_CHECKOUT_MODAL, {
                          price: finalPrice,
                          buttonLabel: `Pay $${formatPriceForDisplay(
                            finalPrice.total_price.value,
                          )}`,
                          onSuccess: async token => {
                            await this.onCheckout(token);
                          },
                          cardRequired: false,
                          defaultName: user?.name ?? '',
                          defaultEmail: user?.email_address ?? '',
                          accountReferralTypes: this.getAccountReferralTypes(),
                          checkoutPriceVariables: ({
                            promo_code: promoCode,
                          }) => {
                            return {
                              account_id: user?.id,
                              promo_code: promoCode,
                              ...checkoutPriceVariables,
                            };
                          },
                        });

                        gtm.trackPageView({
                          url: `${baseUrl}${location.pathname}/virtual/payment`,
                          title: 'Virtual Pageview - Payment',
                          userId: user.id,
                        });
                      }}
                    >
                      <div className="d-flex flex-column">
                        <span>
                          {isPrepaidOnlyAlbumFolder(albumFolder) ? (
                            'Checkout now'
                          ) : (
                            <Fragment>
                              {selectedIdentities.length -
                                identitiesForJoin.length}{' '}
                              <ContextualString
                                id="purchaseSubscriptionText"
                                type={albumFolder.album_type}
                                subtype={albumFolder.album_subtype}
                                defaultMessage="Photo Pass"
                                formatter={s =>
                                  pluralize(
                                    s,
                                    selectedIdentities.length -
                                      identitiesForJoin.length,
                                  )
                                }
                              />
                            </Fragment>
                          )}{' '}
                          (
                          <PriceDisplay
                            showDiscount
                            trimDecimalZeros={false}
                            price={finalPrice?.total_price?.value}
                            oldPrice={Math.max(
                              finalPrice?.total_price?.value +
                                (finalPrice?.total_discount_price?.value ?? 0),
                              getAlbumFolderTotalPrice(selectedIdentities),
                            )}
                          />
                          )
                        </span>
                        {!isPrepaidOnlyAlbumFolder(albumFolder) &&
                          identitiesForJoin.length > 0 && (
                            <span className="text-faded text-xs">
                              {identitiesForJoin.length} Preview Only Members
                            </span>
                          )}
                      </div>
                    </Button>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Button
                      btnBlock
                      disabled={
                        loading || loadingFinalPrice || !!checkoutPriceError
                      }
                      onClick={() => this.onCheckout()}
                      className={prepaidPrintPackagePromoCode ? 'mt-2' : 'mt-1'}
                    >
                      {loading ? (
                        'Loading...'
                      ) : (
                        <div className="d-flex flex-column pos-rel">
                          {allowsPrepaidItems ? (
                            <span>
                              View Purchase Options
                              {prepaidPrintPackagePromoCode && (
                                <Fragment>
                                  {' '}
                                  &amp; Save{' '}
                                  {prepaidPrintPackagePromoCode.discount
                                    .mode === DISCOUNT_MODE.PERCENTAGE
                                    ? `${prepaidPrintPackagePromoCode.discount.value}%`
                                    : `$${formatPriceForDisplay(
                                        prepaidPrintPackagePromoCode.discount
                                          .value,
                                        true,
                                      )}`}
                                  !
                                </Fragment>
                              )}
                            </span>
                          ) : (
                            <Fragment>
                              {hasSearchSubscription ||
                              !albumFolderRequiresPhotoSearchOnboarding(
                                albumFolder,
                              ) ? (
                                <span>Confirm</span>
                              ) : (
                                // Perhaps Promote buying one?
                                <Fragment>
                                  Waldo, Find My Photos!
                                  {isEligibleForShutterbugFreeTrial(user) && (
                                    <Badge
                                      badgeStyle="yellow"
                                      className="album-purchase-free-trial-badge"
                                    >
                                      {SHUTTERBUG_TRIAL_DAYS_FALLBACK}-DAY FREE
                                      TRIAL
                                    </Badge>
                                  )}
                                </Fragment>
                              )}
                            </Fragment>
                          )}
                        </div>
                      )}
                    </Button>
                    {allowsPrepaidItems && (
                      <Button
                        className="mt-1"
                        btnSize="sm"
                        btnStyle="link"
                        disabled={!canCheckout}
                        onClick={e => {
                          this.setState(() => ({
                            skipPrintPackages: true,
                          }));
                          this.onCheckout(e);
                        }}
                      >
                        No thanks, purchase later
                      </Button>
                    )}
                  </Fragment>
                )}
              </div>
            </OnboardingLayoutFooter>
          </Fragment>
        )}

        <DiscoverAlbumFolderJoinTypeSelectionModal
          albumFolder={albumFolder}
          identity={identityToEdit}
          onSelectIdentity={onSelectIdentity}
          onAfterSelect={() => {
            this.setState(() => ({ identityToEdit: null }));
          }}
          forceOpen={!!identityToEdit}
        />

        <AccountUpdatePhoneNumberModalContainer />
        <SiblingDiscountAlertModal
          onRequestClose={() => {
            closeModal(SIBLING_DISCOUNT_ALERT_MODAL);
          }}
          onAddIdentity={() => {
            history.replace(
              `/discover/album-folder/${albumFolder.id}/purchase-subscription`,
            );
          }}
          albumSubtype={albumFolder?.album_subtype}
        />
      </Fragment>
    );
  }
}

export default AlbumFolderPurchaseSummary;
